import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { HOFFMAN_CATEGORIES } from '../hoffmanModel';
import ExportReport from '../components/ExportReport';

// Backward compatibility - map old emotions to Hoffman categories
const LEGACY_EMOTION_MAPPING = {
  'Joy': 'Aliveness/Joy',
  'Excitement': 'Aliveness/Joy',
  'Serenity': 'Accepting/Open',
  'Contentment': 'Accepting/Open',
  'Interest': 'Curious',
  'Neutral': 'Accepting/Open',
  'Sadness': 'Despair/Sad',
  'Anxiety': 'Fear',
  'Anger': 'Angry/Annoyed',
  'Fatigue': 'Stressed/Tense',
  'Melancholy': 'Despair/Sad',
  'Overwhelm': 'Stressed/Tense'
};

export default function History({ user, onLogout }) {
  const [moodHistory, setMoodHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // week, month, all

  useEffect(() => {
    fetchMoodHistory();
  }, [timeRange]);

  const fetchMoodHistory = async () => {
    setLoading(true);
    try {
      const limit = timeRange === 'week' ? 50 : timeRange === 'month' ? 200 : 500;
      const res = await axios.get(`${API}/moods?limit=${limit}`);
      
      // Process data for the chart
      const processedData = res.data.map((mood, index) => {
        // Determine category and valence
        let category, valence, emotionName;
        
        if (mood.primary_category) {
          // New Hoffman format
          category = mood.primary_category;
          emotionName = mood.specific_feeling;
          const categoryData = HOFFMAN_CATEGORIES[category];
          valence = categoryData ? categoryData.valence : 0;
        } else {
          // Legacy format - map to Hoffman
          const legacyEmotion = mood.mood_name;
          category = LEGACY_EMOTION_MAPPING[legacyEmotion] || 'Accepting/Open';
          emotionName = legacyEmotion;
          const categoryData = HOFFMAN_CATEGORIES[category];
          valence = categoryData ? categoryData.valence : 0;
        }
        
        const intensity = mood.emotional_intensity || mood.intensity || 5;
        const signedIntensity = valence >= 0 ? intensity : -intensity;
        
        return {
          id: mood.id,
          timestamp: new Date(mood.timestamp),
          displayDate: new Date(mood.timestamp).toLocaleDateString(),
          displayTime: new Date(mood.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          emotion: emotionName,
          category: category,
          intensity: intensity,
          signedIntensity: signedIntensity,
          valence: valence,
          isPositive: valence > 0,
          isNegative: valence < 0,
          reason: mood.reason,
          bodySensations: mood.body_sensations || []
        };
      }).reverse(); // Reverse to show oldest to newest
      
      setMoodHistory(processedData);
    } catch (error) {
      console.error('Failed to fetch mood history', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-md border-2 border-stone-200 rounded-xl p-4 shadow-xl">
          <p className="font-semibold text-base mb-1">{data.emotion}</p>
          <p className="text-sm text-warm-charcoal/70 mb-2">{data.displayDate} at {data.displayTime}</p>
          <p className="text-xs text-warm-charcoal/60">Intensity: <span className="font-bold">{data.intensity}/10</span></p>
          {data.reason && (
            <p className="text-xs text-warm-charcoal/50 mt-2 italic max-w-xs">"{data.reason}"</p>
          )}
        </div>
      );
    }
    return null;
  };

  const getEmotionColor = (category) => {
    const categoryData = HOFFMAN_CATEGORIES[category];
    return categoryData ? categoryData.color : '#78716C';
  };

  const positiveMoods = moodHistory.filter(m => m.isPositive).length;
  const negativeMoods = moodHistory.filter(m => m.isNegative).length;
  const neutralMoods = moodHistory.filter(m => !m.isPositive && !m.isNegative).length;

  return (
    <div className="h-screen flex flex-col pb-20 bg-gradient-to-b from-white via-stone-50 to-stone-100 relative">
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-6 max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold mb-1 text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
              Emotional Journey
            </h1>
            <p className="text-sm text-warm-charcoal/60 font-light">
              Track your emotional patterns over time
            </p>
          </div>
          <ExportReport />
        </div>

        {/* Time Range Selector */}
        <div className="zen-card p-6 mb-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-hunter-green" />
              <span className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">Time Range</span>
            </div>
            <div className="flex gap-2">
              {['week', 'month', 'all'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    timeRange === range
                      ? 'bg-hunter-green text-white shadow-lg'
                      : 'bg-white/50 text-warm-charcoal/60 hover:bg-white border border-stone-200'
                  }`}
                >
                  {range === 'week' ? 'Last Week' : range === 'month' ? 'Last Month' : 'All Time'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-4 md:py-6">
            <p className="text-warm-charcoal/60">Loading your emotional journey...</p>
          </div>
        )}

        {!loading && moodHistory.length === 0 && (
          <div className="zen-card p-12 text-center">
            <p className="text-warm-charcoal/60 mb-2">No mood entries yet</p>
            <p className="text-sm text-warm-charcoal/40">Start tracking your emotions on the dashboard</p>
          </div>
        )}

        {!loading && moodHistory.length > 0 && (
          <>
            {/* Mood Graph - MOVED TO TOP */}
            <div className="zen-card p-8 mb-3">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Emotional Intensity Over Time
                </h2>
                <div className="space-y-1">
                  <p className="text-sm text-warm-charcoal/70">
                    <strong>Timeline:</strong> {timeRange === 'week' ? 'Last 7 days' : timeRange === 'month' ? 'Last 30 days' : 'All recorded emotions'}
                  </p>
                  <p className="text-sm text-warm-charcoal/60">
                    • <strong>Positive emotions</strong> (joy, gratitude, love) are shown <strong>above the line</strong>
                  </p>
                  <p className="text-sm text-warm-charcoal/60">
                    • <strong>Challenging emotions</strong> (sadness, stress, fear) are shown <strong>below the line</strong>
                  </p>
                  <p className="text-sm text-warm-charcoal/60">
                    • Hover over points to see details (emotion, date, intensity, notes)
                  </p>
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={moodHistory}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fill: '#78716C', fontSize: 12 }}
                    stroke="#E7E5E4"
                  />
                  <YAxis 
                    domain={[-10, 10]}
                    ticks={[-10, -5, 0, 5, 10]}
                    tick={{ fill: '#78716C', fontSize: 12 }}
                    stroke="#E7E5E4"
                    label={{ value: 'Intensity', angle: -90, position: 'insideLeft', fill: '#78716C' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="#3A5A40" strokeWidth={2} />
                  <Line 
                    type="monotone" 
                    dataKey="signedIntensity" 
                    stroke="#3A5A40" 
                    strokeWidth={3}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={6}
                          fill={getEmotionColor(payload.category)}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      );
                    }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Stats - MOVED AFTER GRAPH */}
            <div className="grid md:grid-cols-3 gap-6 mb-3">
              <div className="zen-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-hunter-green/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-hunter-green" />
                  </div>
                  <span className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">Positive</span>
                </div>
                <p className="text-3xl font-semibold">{positiveMoods}</p>
                <p className="text-xs text-warm-charcoal/50 mt-1">
                  {((positiveMoods / moodHistory.length) * 100).toFixed(0)}% of entries
                </p>
              </div>

              <div className="zen-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-stone-200/50 flex items-center justify-center">
                    <div className="w-5 h-0.5 bg-warm-charcoal/60" />
                  </div>
                  <span className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">Neutral</span>
                </div>
                <p className="text-3xl font-semibold">{neutralMoods}</p>
                <p className="text-xs text-warm-charcoal/50 mt-1">
                  {((neutralMoods / moodHistory.length) * 100).toFixed(0)}% of entries
                </p>
              </div>

              <div className="zen-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-warm-charcoal/80 uppercase tracking-wider">Challenging</span>
                </div>
                <p className="text-3xl font-semibold">{negativeMoods}</p>
                <p className="text-xs text-warm-charcoal/50 mt-1">
                  {((negativeMoods / moodHistory.length) * 100).toFixed(0)}% of entries
                </p>
              </div>
            </div>

            {/* Recent Entries List - MOVED TO END */}
            <div className="zen-card p-8 mt-8">
              <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Recent Entries
              </h2>
              <div className="space-y-3">
                {moodHistory.slice(-10).reverse().map((mood) => (
                  <div key={mood.id} className="p-4 rounded-xl bg-white/50 border border-stone-100 hover:border-hunter-green/30 transition-all duration-300">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-3 h-3 rounded-full mt-1.5" 
                          style={{ backgroundColor: getEmotionColor(mood.category) }}
                        />
                        <div>
                          <p className="font-semibold text-base">{mood.emotion}</p>
                          <p className="text-xs text-warm-charcoal/50 mt-0.5">{mood.category}</p>
                          <p className="text-sm text-warm-charcoal/60 mt-0.5">
                            {mood.displayDate} at {mood.displayTime}
                          </p>
                          {mood.bodySensations && mood.bodySensations.length > 0 && (
                            <p className="text-xs text-warm-charcoal/50 mt-2">
                              Body: {mood.bodySensations.map(s => s.sensation).join(', ')}
                            </p>
                          )}
                          {mood.reason && (
                            <p className="text-sm text-warm-charcoal/50 mt-2 italic">"{mood.reason}"</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: getEmotionColor(mood.category) }}>
                          {mood.intensity}
                        </p>
                        <p className="text-xs text-warm-charcoal/40">/10</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <UnifiedMenu active="history" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />
    </div>
  );
}
